import React, { useState, useEffect } from 'react';
import Layout from '../layout/Layout';
import { usePortfolio } from '../../context/PortfolioContext';
import { usePageContent } from '../../context/ContentContext';

const Portfolio = () => {
    const { projects, isLoading } = usePortfolio();
    const { t, g } = usePageContent('portfolio');
    const [selectedCategory, setSelectedCategory] = useState('ready-to-wear');
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const categories = [
        { id: 'ready-to-wear', name: t('intro.categoryReadyToWear') },
        { id: 'press', name: t('intro.categoryRunway') },
        { id: 'merchandise', name: t('intro.categoryMerchandise') }
    ];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        setFilteredProjects(projects.filter(project => project.category === selectedCategory));
    }, [selectedCategory, projects]);

    // Reset image index when selecting a new project
    useEffect(() => {
        setCurrentImageIndex(0);
    }, [selectedProject]);

    const handlePrevImage = (e) => {
        e.stopPropagation();
        if (selectedProject && selectedProject.images) {
            setCurrentImageIndex(prev =>
                prev === 0 ? selectedProject.images.length - 1 : prev - 1
            );
        }
    };

    const handleNextImage = (e) => {
        e.stopPropagation();
        if (selectedProject && selectedProject.images) {
            setCurrentImageIndex(prev =>
                prev === selectedProject.images.length - 1 ? 0 : prev + 1
            );
        }
    };

    if (isLoading) {
        return (
            <Layout>
                <div className="min-h-screen flex items-center justify-center">
                    <div className="text-gray-500">{t('intro.loadingText')}</div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            {/* Hero Section */}
            <section className="relative h-[70vh] overflow-hidden">
                <img
                    src={t('hero.image')}
                    alt={t('hero.title')}
                    className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Lighter gradient overlay for navbar contrast */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/15 to-transparent"></div>
                <div className="absolute inset-0 bg-black bg-opacity-25 flex items-center justify-center">
                    <h1 className="font-poppins font-light text-h1 leading-[60pt] tracking-[-0.02em] text-white text-center px-4">
                        {t('hero.title')}
                    </h1>
                </div>
            </section>

            {/* Portfolio Section */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center mb-16">
                        <h2 className="font-poppins font-light text-h2 leading-[32pt] tracking-[-0.02em] mb-8 text-primary-dark-red">
                            {t('intro.title')}
                        </h2>
                        <p className="font-poppins font-normal text-body leading-[13pt] tracking-[0.01em] mb-12">
                            {t('intro.body')}
                        </p>

                        {/* Category Filters */}
                        <div className="flex flex-wrap justify-center gap-4 mb-16">
                            {categories.map(category => (
                                <button
                                    key={category.id}
                                    onClick={() => setSelectedCategory(category.id)}
                                    className={`px-6 py-3 transition-colors font-poppins font-semibold text-h3 ${
                                        selectedCategory === category.id
                                            ? 'bg-primary-dark-red text-white'
                                            : 'bg-background-cool-blue text-gray-700 hover:bg-accent-powder-green'
                                    }`}
                                >
                                    {category.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Portfolio Grid - Vertical Images */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {filteredProjects.map(project => (
                            <div
                                key={project.id}
                                className="bg-white shadow-sm overflow-hidden group cursor-pointer"
                                onClick={() => setSelectedProject(project)}
                            >
                                <div className="relative overflow-hidden" style={{ paddingBottom: '150%' }}>
                                    <img
                                        src={project.images && project.images[0] ? project.images[0] : '/images/placeholder.jpg'}
                                        alt={project.title}
                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                    />
                                    {/* Image count badge */}
                                    {project.images && project.images.length > 1 && (
                                        <div className="absolute top-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                                            {project.images.length} images
                                        </div>
                                    )}
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300 flex items-center justify-center">
                                        <span className="text-white font-poppins font-semibold text-h3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                            {t('intro.viewProjectText')}
                                        </span>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <h3 className="font-poppins font-semibold text-h3 mb-2">{project.title}</h3>
                                    {(project.client || project.year) && (
                                        <p className="font-poppins font-normal text-sub text-gray-600">
                                            {project.client}
                                            {project.client && project.year && ' • '}
                                            {project.year}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {filteredProjects.length === 0 && (
                        <div className="text-center py-16">
                            <p className="font-poppins font-normal text-body text-gray-500">{t('intro.emptyText')}</p>
                        </div>
                    )}
                </div>
            </section>

            {/* Project Modal with Image Gallery */}
            {selectedProject && (
                <div
                    className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-75 flex items-center justify-center p-4"
                    onClick={() => setSelectedProject(null)}
                >
                    <div
                        className="bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-lg"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="sticky top-0 z-10 flex justify-end p-4 bg-white border-b">
                            <button
                                onClick={() => setSelectedProject(null)}
                                className="text-gray-500 hover:text-gray-700 p-1"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-6 pt-0">
                            {/* Image Gallery */}
                            <div className="mb-6 relative">
                                {selectedProject.images && selectedProject.images.length > 0 ? (
                                    <>
                                        <img
                                            src={selectedProject.images[currentImageIndex]}
                                            alt={`${selectedProject.title} - Image ${currentImageIndex + 1}`}
                                            className="w-full h-auto"
                                        />

                                        {/* Navigation arrows - only show if multiple images */}
                                        {selectedProject.images.length > 1 && (
                                            <>
                                                <button
                                                    onClick={handlePrevImage}
                                                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-colors"
                                                >
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={handleNextImage}
                                                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-colors"
                                                >
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </button>

                                                {/* Image counter */}
                                                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm">
                                                    {currentImageIndex + 1} / {selectedProject.images.length}
                                                </div>
                                            </>
                                        )}

                                        {/* Thumbnail strip - show if multiple images */}
                                        {selectedProject.images.length > 1 && (
                                            <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                                                {selectedProject.images.map((img, index) => (
                                                    <button
                                                        key={index}
                                                        onClick={() => setCurrentImageIndex(index)}
                                                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                                                            index === currentImageIndex
                                                                ? 'border-primary-red'
                                                                : 'border-transparent hover:border-gray-300'
                                                        }`}
                                                    >
                                                        <img
                                                            src={img}
                                                            alt={`Thumbnail ${index + 1}`}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="w-full h-64 bg-gray-200 flex items-center justify-center">
                                        <span className="text-gray-500">No images available</span>
                                    </div>
                                )}
                            </div>

                            <h2 className="font-poppins font-light text-h2 leading-[32pt] tracking-[-0.02em] mb-4">
                                {selectedProject.title}
                            </h2>
                            {(selectedProject.client || selectedProject.year || selectedProject.category) && (
                                <div className="grid md:grid-cols-3 gap-8 mb-6">
                                    {selectedProject.client && (
                                        <div>
                                            <h4 className="font-poppins font-semibold text-h3 text-gray-500 mb-1">CLIENT</h4>
                                            <p className="font-poppins font-normal text-body">{selectedProject.client}</p>
                                        </div>
                                    )}
                                    {selectedProject.year && (
                                        <div>
                                            <h4 className="font-poppins font-semibold text-h3 text-gray-500 mb-1">YEAR</h4>
                                            <p className="font-poppins font-normal text-body">{selectedProject.year}</p>
                                        </div>
                                    )}
                                    <div>
                                        <h4 className="font-poppins font-semibold text-h3 text-gray-500 mb-1">CATEGORY</h4>
                                        <p className="font-poppins font-normal text-body capitalize">{selectedProject.category === 'press' ? 'Runway' : selectedProject.category.replace(/-/g, ' ')}</p>
                                    </div>
                                </div>
                            )}
                            {selectedProject.description && (
                                <div className="prose max-w-none">
                                    <p className="font-poppins font-normal text-body leading-[13pt] tracking-[0.01em]">
                                        {selectedProject.description}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Call to Action */}
            <section className="py-20 bg-primary-dark-red text-white">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="font-poppins font-light text-h2 leading-[32pt] tracking-[-0.02em] mb-6">
                        {t('cta.title')}
                    </h2>
                    <p className="font-poppins font-normal text-body leading-[13pt] tracking-[0.01em] max-w-2xl mx-auto mb-10">
                        {t('cta.body')}
                    </p>
                    <a
                        href={`mailto:${g('contact.email')}`}
                        className="inline-block px-8 py-4 bg-primary-red text-white hover:bg-pink-600 transition-colors duration-300 font-poppins font-semibold text-h3"
                    >
                        {t('cta.buttonText')}
                    </a>
                </div>
            </section>
        </Layout>
    );
};

export default Portfolio;